import { Dayjs } from "dayjs";
import { CouponScope } from "./Coupon";
import { LineItemType } from "./LineItem";
import { AllFilter, AndFilter, EqualsFilter, InFilter, InProductScheduleFilter, OrFilter } from "./RuleFilter";
import { SelectedExperiences } from "./SelectedExperiences";
import { Tag } from "./Tag";

export interface PurchaseRule {
    id?: ID;
    name?: string;
    permanentId?: ID;
    filter?: AndFilter | OrFilter | InFilter | InProductScheduleFilter | AllFilter | null;
    deletedAt?: Dayjs;
    actions: PurchaseRuleAction[];
    seller?: { id: ID };
    tags?: Tag[];
    experiences?: SelectedExperiences;
    _cascadeToClones?: string[];
    meta?: PurchaseRuleMeta;
    processActionsForIgnoredValidations?: boolean;
    shouldCheckFieldValueForIgnoredValidations?: boolean;
    createdAt?: Dayjs;
}

export interface PurchaseRuleMeta {
    schedules?: string[];
    variationWeight?: number;
    tieredPricing?: {
        min: number;
        max?: number;
    };
    addons?: boolean;
    demographics?: string[];
    merchandise?: AddonActionItem[];
    product?: ID;
    // productId?: ID;
    subProductId?: ID;
}

interface AddonActionItem {
    actionId?: ID;
    experienceId?: ID;
}

export interface PurchaseRuleAction {
    id?: ID;
    object: PurchaseRuleActionObject;
    modifiers?: PurchaseRuleModifier[];
    shortcut?: PurchaseRuleShortcut;
    filter?: EqualsFilter | AndFilter; // Can also contain other filters
    template?: { id: ID };
    _remove?: boolean;
}

export interface PurchaseRuleModifier {
    aggregator:
        | BaseAggregator
        | PercentAggregator
        | ReduceAggregator
        | DifferenceAggregator
        | MaxAggregator
        | VoucherDiscountAggregator;
    field: string;
    id?: ID;
    object?: string;
    operation: PurchaseRuleModifierOperation; // TODO: Change this to enum
}

export interface BaseAggregator {
    id?: ID;
    object: AggregatorObject;
    operator?: AggregatorOperator; // enum
    percentValue?: number;
    operand?: number | string | ReduceAggregatorOperand;
    operands?: BaseAggregator[];
}

export interface PercentAggregator extends BaseAggregator {
    percentValue: number;
    operand: number;
}

export interface ReduceAggregator extends BaseAggregator {
    percentValue: number;
}

export interface ReduceAggregatorOperand {
    object: AggregatorObject.REDUCE_AGGREGATOR;
    itemFilter: EqualsFilter;
    operand: string;
    itemAggregator: {
        object: "return_field_aggregator";
        operand: string;
    };
}

export interface PurchaseRuleModifierAggregator {
    id?: ID;
    object: AggregatorObject;
    operator?: AggregatorOperator; // enum
    percentValue?: number | null;
    operand: number | string | ReduceAggregatorOperand;
}

export interface PercentAggregator extends BaseAggregator {
    percentValue: number;
    operand: number;
}

export interface ReduceAggregator extends BaseAggregator {
    percentValue: number;
    operand: ReduceAggregatorOperand;
}

export interface DifferenceAggregator extends BaseAggregator {
    id?: ID;
    object: AggregatorObject;
    operator?: AggregatorOperator; // enum
    operands: BaseAggregator[];
}

export interface MaxAggregator extends BaseAggregator {
    operands: BaseAggregator[];
}

export interface VoucherDiscountAggregator extends BaseAggregator {
    coupon: object;
}

export interface ReduceAggregatorOperand {
    object: AggregatorObject.REDUCE_AGGREGATOR;
    itemFilter: EqualsFilter;
    operand: string;
    itemAggregator: {
        object: "return_field_aggregator";
        operand: string;
    };
}

export enum AggregatorOperator {
    PERCENTAGE = "percent",
    MULTIPLY = "multiply",
}

export enum PurchaseRuleModifierOperation {
    SET = "set",
    INC = "inc",
}

export enum AggregatorObject {
    RETURN_VALUE_AGGREGATOR = "return_value_aggregator",
    RETURN_FIELD_AGGREGATOR = "return_field_aggregator",
    PERCENT_AGGREGATOR = "percent_aggregator",
    REDUCE_AGGREGATOR = "reduce_aggregator",
    DIFFERENCE_AGGREGATOR = "difference_aggregator",
    MULTIPLY_AGGREGATOR = "multiply_aggregator",
    SUM_AGGREGATOR = "sum_aggregator",
    MAX_AGGREGATOR = "max_aggregator",
    VOUCHER_DISCOUNT_AGGREGATOR = "voucher_discount_aggregator",
}

export enum PurchaseRuleActionObject {
    LINE_ITEM_ACTION = "line_item_action",
    INSERT_LINE_ITEM_ACTION = "insert_line_item_action",
    UPDATE_LINE_ITEM_ACTION = "update_line_item_action",
    UPSERT_LINE_ITEM_ACTION = "upsert_line_item_action",
    COPY_LINE_ITEM_ACTION = "copy_line_item_action",
}

export interface PurchaseRuleShortcut {
    amount: number;
    options?: PurchaseRuleShortcutDiscountOption | PurchaseRuleShortcutBXGYOption;
    type: PurchaseRuleShortcutType;
    field?: string;
    operation?: PurchaseRuleModifierOperation;
}

export enum PurchaseRuleShortcutType {
    ABSOLUTE_PER_UNIT = "absolute_per_unit",
    ABSOLUTE_PER_PURCHASE = "absolute_per_purchase",
    ABSOLUTE_PER_UNIT_PER_DAY = "absolute_per_unit_per_day",
    ABSOLUTE_FINAL_PRICE = "absolute_final_price",
    ABSOLUTE_DISCOUNT_PER_UNIT = "absolute_discount_per_unit",
    PERCENT = "percent",
    BXGY_PERCENT = "bxgy_percent",
    BXGY_ABSOLUTE = "bxgy_absolute",
}

export interface PurchaseRuleShortcutDiscountOption {
    // One of templateCodes or templateIds should exist
    templateCodes?: string[] | PurchaseRuleShortcutTemplateCode[];
    templateIds?: ID[];
    minUnitCount?: number;
    maxUnitCount?: number;
    templateTypes?: string[] | LineItemType[];
    scope?: CouponScope;
    context?: string;
}

export interface PurchaseRuleShortcutBXGYOption {
    // One of templateCodes, templateIds, or qualifyingLineItemTemplates should exist
    templateCodes?: string[] | PurchaseRuleShortcutTemplateCode[];
    templateIds?: ID[];
    qualifyingLineItemTemplates?: Array<{ id: ID }>;
    qualifyingQuantity: number;
    eligibleQuantity?: number;
    minUnitCount?: number;
    maxUnitCount?: number;
    templateTypes?: LineItemType[];
}

// One for now, more to come later
export enum PurchaseRuleShortcutTemplateCode {
    DEMOGRAPHIC = "demographic_subtotal",
    EQUIPMENT = "equipment_subtotal",
    ADDON = "merchandise_subtotal",
    UNIT = "unit_subtotal",
    PRE_TAX_SUBTOTAL = "pre_tax_subtotal",
    PRE_TAX_FEE_SUBTOTAL = "pre_tax_fee_subtotal",
    PICKUP_SUBTOTAL = "pickup_subtotal",
    DISCOUNT_SUBTOTAL = "discount_subtotal",
    MEMBERSHIP_SUBTOTAL = "membership_subtotal",
    WARE_SUBTOTAL = "ware_subtotal",
}

export type AnyAggregator = BaseAggregator | PercentAggregator | ReduceAggregator | DifferenceAggregator;
